import React from 'react';
import { Box, Typography, Link, Avatar, Grid, Paper, Divider, Chip } from '@mui/material';
import { 
    Email, 
    Phone, 
    WhatsApp, 
    LocationOn, 
    Language,
    LinkedIn,
    Instagram,
    Twitter,
    Facebook,
    YouTube,
    GitHub,
    GetApp,
    Business,
    Person,
    Download
} from '@mui/icons-material';
import config from '../../../config';
import { getFullUrl } from '../../../utils/urlUtils';

const MinimalistTheme = ({ card, onAnalyticsClick, qrCodeValue, downloadVCard }) => {
    const primaryColor = '#00ff88'; // Bright green accent
    const backgroundColor = '#0a0a0a'; // Deep black
    const surfaceColor = '#1a1a1a'; // Dark surface
    const cardColor = '#252525'; // Card background
    const textPrimary = '#ffffff';
    const textSecondary = '#b0b0b0';
    const borderColor = '#333333';

    // Helper function to get image URL - handles both File objects and URLs
    const getImageUrl = (value) => {
        if (!value) return null;
        if (value instanceof File) return URL.createObjectURL(value);
        if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('blob'))) return value;
        if (typeof value === 'string') return getFullUrl(value);
        return null;
    };

    const getContactIcon = (type) => {
        const iconProps = { sx: { color: primaryColor, fontSize: 20 } };
        switch (type?.toLowerCase()) {
            case 'email': return <Email {...iconProps} />;
            case 'phone': return <Phone {...iconProps} />;
            case 'whatsapp': return <WhatsApp {...iconProps} />;
            default: return <Email {...iconProps} />;
        }
    };

    const getSocialIcon = (platform) => {
        const iconProps = { sx: { color: primaryColor, fontSize: 24 } };
        switch (platform?.toLowerCase()) {
            case 'linkedin': return <LinkedIn {...iconProps} />;
            case 'instagram': return <Instagram {...iconProps} />;
            case 'twitter': return <Twitter {...iconProps} />;
            case 'facebook': return <Facebook {...iconProps} />;
            case 'youtube': return <YouTube {...iconProps} />;
            case 'github': return <GitHub {...iconProps} />;
            default: return <Language {...iconProps} />;
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor,
                color: textPrimary,
                position: 'relative',
            }}
        >
            {/* Cover Image Section */}
            {card?.cover_photo_url && (
                <Box
                    sx={{
                        height: { xs: 200, md: 300 },
                        backgroundImage: `url(${getImageUrl(card.cover_photo_url)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'flex-end',
                        p: 2,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `linear-gradient(to bottom, transparent 0%, ${backgroundColor}80 100%)`,
                        }
                    }}
                >
                    {/* Company Logo */}
                    {card?.company_logo_url && (
                        <Box
                            sx={{
                                position: 'relative',
                                zIndex: 2,
                                width: { xs: 60, md: 80 },
                                height: { xs: 60, md: 80 },
                                borderRadius: '12px',
                                overflow: 'hidden',
                                border: `2px solid ${primaryColor}`,
                                backgroundColor: backgroundColor,
                            }}
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundImage: `url(${getImageUrl(card.company_logo_url)})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                }}
                            />
                        </Box>
                    )}
                </Box>
            )}

            {/* Hero Section */}
            <Box
                sx={{
                    py: { xs: 4, md: 6 },
                    px: { xs: 2, md: 4 },
                    textAlign: 'center',
                    borderBottom: `1px solid ${borderColor}`,
                    mt: card?.cover_photo_url ? { xs: -6, md: -8 } : 0,
                    position: 'relative',
                    zIndex: 3,
                }}
            >
                {/* Profile Image */}
                <Box
                    sx={{
                        position: 'relative',
                        display: 'inline-block',
                        mb: 3,
                        animation: 'fadeIn 0.8s ease-out',
                        '@keyframes fadeIn': {
                            '0%': { opacity: 0, transform: 'translateY(20px)' },
                            '100%': { opacity: 1, transform: 'translateY(0)' }
                        }
                    }}
                >
                    <Box
                        sx={{
                            width: { xs: 120, md: 150 },
                            height: { xs: 120, md: 150 },
                            borderRadius: '12px',
                            overflow: 'hidden',
                            border: `3px solid ${primaryColor}`,
                            mx: 'auto',
                            transition: 'transform 0.3s ease',
                            backgroundColor: cardColor,
                            '&:hover': {
                                transform: 'scale(1.05)',
                            }
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                backgroundImage: `url(${getImageUrl(card?.profile_picture_url)})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}
                        />
                    </Box>
                    
                    {/* Online Status Indicator */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 5,
                            right: 5,
                            width: 25,
                            height: 25,
                            borderRadius: '50%',
                            backgroundColor: primaryColor,
                            border: `3px solid ${backgroundColor}`,
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                                '0%': { boxShadow: `0 0 0 0 ${primaryColor}66` },
                                '70%': { boxShadow: `0 0 0 10px ${primaryColor}00` },
                                '100%': { boxShadow: `0 0 0 0 ${primaryColor}00` }
                            }
                        }}
                    />
                </Box>

                {/* Name and Title */}
                <Typography
                    variant="h3"
                    sx={{
                        fontSize: { xs: '1.2rem', md: '2.0rem' },
                        fontWeight: 700,
                        mb: 1,
                        color: textPrimary,
                        animation: 'slideUp 0.8s ease-out 0.2s both',
                        '@keyframes slideUp': {
                            '0%': { opacity: 0, transform: 'translateY(30px)' },
                            '100%': { opacity: 1, transform: 'translateY(0)' }
                        },
                        lineHeight: 1.2,
                        textAlign: 'center',
                        px: { xs: 1, md: 0 },
                    }}
                >
                    {card?.name}
                </Typography>
                
                <Typography
                    variant="h5"
                    sx={{
                        fontSize: { xs: '0.9rem', md: '1.1rem' },
                        fontWeight: 500,
                        color: primaryColor,
                        mb: 2,
                        animation: 'slideUp 0.8s ease-out 0.4s both',
                        textAlign: 'center',
                        px: { xs: 1, md: 0 },
                    }}
                >
                    {card?.designation}
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        color: textSecondary,
                        mb: 3,
                        maxWidth: { xs: '100%', md: '600px' },
                        mx: 'auto',
                        lineHeight: 1.6,
                        animation: 'slideUp 0.8s ease-out 0.6s both',
                        fontSize: { xs: '0.8rem', md: '0.9rem' },
                        textAlign: 'center',
                        px: { xs: 1, md: 0 },
                    }}
                >
                    {card?.company_name && `${card.company_name} • `}
                    {card?.address}
                </Typography>

                {/* Contact Buttons */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        gap: { xs: 1.5, md: 2 }, 
                        justifyContent: 'center', 
                        flexWrap: 'wrap',
                        animation: 'slideUp 0.8s ease-out 0.8s both',
                        px: { xs: 1, md: 0 },
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2.5, md: 3 },
                            py: { xs: 1.2, md: 1.5 },
                            backgroundColor: primaryColor,
                            color: backgroundColor,
                            borderRadius: '8px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: { xs: '0.8rem', md: '0.9rem' },
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 20px ${primaryColor}40`,
                            }
                        }}
                        onClick={onAnalyticsClick}
                    >
                        <Email sx={{ fontSize: { xs: 16, md: 18 } }} />
                        Contact Me
                    </Box>
                </Box>
            </Box>

            {/* Contact Details Section */}
            {card?.contacts && card.contacts.length > 0 && (
                <Box
                    sx={{
                        py: { xs: 3, md: 4 },
                        px: { xs: 2, md: 4 },
                        borderBottom: `1px solid ${borderColor}`,
                    }}
                >
                    <Box sx={{ maxWidth: { xs: '100%', md: '600px' }, mx: 'auto' }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.3rem', md: '2rem' },
                                fontWeight: 700,
                                mb: { xs: 2, md: 3 },
                                color: primaryColor,
                                textAlign: 'center',
                            }}
                        >
                            Contact Details
                        </Typography>

                        <Box sx={{ mb: { xs: 2, md: 3 } }}>
                            {card.contacts.map((contact, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: { xs: 1.5, md: 2 },
                                        p: { xs: 1.5, md: 2 },
                                        borderRadius: { xs: '6px', md: '8px' },
                                        backgroundColor: cardColor,
                                        border: `1px solid ${borderColor}`,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: primaryColor,
                                            transform: { xs: 'translateX(3px)', md: 'translateX(5px)' },
                                        }
                                    }}
                                >
                                    {getContactIcon(contact.type)}
                                    <Typography
                                        sx={{
                                            color: textPrimary,
                                            ml: 2,
                                            fontSize: { xs: '0.8rem', md: '0.9rem' },
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {contact.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Save Contact Button */}
                        <Box sx={{ textAlign: 'center' }}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    px: { xs: 2.5, md: 3 },
                                    py: { xs: 1.2, md: 1.5 },
                                    border: `2px solid ${primaryColor}`,
                                    color: primaryColor,
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    alignItems: 'center',
                                    gap: 1,
                                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                                    '&:hover': {
                                        backgroundColor: primaryColor,
                                        color: backgroundColor,
                                        transform: 'translateY(-2px)',
                                    }
                                }}
                                onClick={downloadVCard}
                            >
                                <Download sx={{ fontSize: { xs: 16, md: 18 } }} />
                                Save Contact
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* About Section */}
            {card?.contents && card.contents.length > 0 && (
                <Box
                    sx={{
                        py: { xs: 3, md: 6 },
                        px: { xs: 2, md: 4 },
                        borderBottom: `1px solid ${borderColor}`,
                    }}
                >
                    <Box sx={{ maxWidth: { xs: '100%', md: '800px' }, mx: 'auto' }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.3rem', md: '2rem' },
                                fontWeight: 700,
                                mb: { xs: 2, md: 3 },
                                color: primaryColor,
                                textAlign: 'center',
                            }}
                        >
                            About Me
                        </Typography>
                        
                        {card.contents.map((content, index) => (
                            <Box key={index} sx={{ mb: { xs: 2, md: 3 } }}>
                                {content.field_name && (
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: textPrimary,
                                            fontWeight: 600,
                                            mb: { xs: 1, md: 2 },
                                            fontSize: { xs: '1.1rem', md: '1.25rem' },
                                        }}
                                    >
                                        {content.field_name}
                                    </Typography>
                                )}
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: textSecondary,
                                        lineHeight: 1.7,
                                        fontSize: { xs: '0.8rem', md: '0.9rem' },
                                    }}
                                    dangerouslySetInnerHTML={{ __html: content.field_value }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Batches Section */}
            {card?.batches && card.batches.length > 0 && (
                <Box
                    sx={{
                        py: { xs: 3, md: 6 },
                        px: { xs: 2, md: 4 },
                        borderBottom: `1px solid ${borderColor}`,
                    }}
                >
                    <Box sx={{ maxWidth: { xs: '100%', md: '800px' }, mx: 'auto' }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.3rem', md: '2rem' },
                                fontWeight: 700,
                                mb: { xs: 3, md: 4 },
                                color: primaryColor,
                                textAlign: 'center',
                            }}
                        >
                            Batches
                        </Typography>

                        <Grid container spacing={3}>
                            {card.batches.map((batch, index) => (
                                <Grid item xs={12} sm={6} md={4} key={batch.id || index}>
                                    <Paper
                                        sx={{
                                            p: { xs: 2, md: 3 },
                                            borderRadius: 2,
                                            textAlign: 'center',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: 3,
                                            },
                                        }}
                                    >
                                        {batch.image_url && (
                                            <Box sx={{ mb: 2 }}>
                                                <img
                                                    src={batch.image_url}
                                                    alt={batch.caption || 'Batch'}
                                                    style={{
                                                        width: '60px',
                                                        height: '60px',
                                                        borderRadius: '12px',
                                                        objectFit: 'cover',
                                                        margin: '0 auto',
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        {batch.caption && (
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: textPrimary,
                                                    fontWeight: 600,
                                                    fontSize: { xs: '0.9rem', md: '1rem' },
                                                }}
                                            >
                                                {batch.caption}
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Box>
            )}

            {/* Gallery Section */}
            {card?.product_images && card.product_images.length > 0 && (
                <Box
                    sx={{
                        py: { xs: 3, md: 6 },
                        px: { xs: 2, md: 4 },
                        borderBottom: `1px solid ${borderColor}`,
                    }}
                >
                    <Box sx={{ maxWidth: { xs: '100%', md: '1000px' }, mx: 'auto' }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.3rem', md: '2rem' },
                                fontWeight: 700,
                                mb: { xs: 3, md: 4 },
                                color: primaryColor,
                                textAlign: 'center',
                            }}
                        >
                            Portfolio
                        </Typography>

                        <Grid container spacing={{ xs: 2, md: 3 }}>
                            {card.product_images.map((image, index) => (
                                <Grid item xs={6} sm={6} md={4} key={index}>
                                    <Paper
                                        sx={{
                                            borderRadius: { xs: '8px', md: '12px' },
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            backgroundColor: cardColor,
                                            border: `1px solid ${borderColor}`,
                                            '&:hover': {
                                                transform: { xs: 'translateY(-3px)', md: 'translateY(-5px)' },
                                                boxShadow: `0 15px 30px ${primaryColor}20`,
                                                borderColor: primaryColor,
                                            }
                                        }}
                                        onClick={onAnalyticsClick}
                                    >
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: { xs: 150, md: 200 },
                                                backgroundImage: `url(${getImageUrl(image.url)})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                backgroundColor: cardColor,
                                                position: 'relative',
                                                '&:hover::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: `linear-gradient(45deg, ${primaryColor}30, transparent)`,
                                                }
                                            }}
                                        />
                                        {image.caption && (
                                            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: textPrimary,
                                                        fontWeight: 500,
                                                        fontSize: { xs: '0.8rem', md: '0.875rem' },
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {image.caption}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Box>
            )}

            {/* Social & Company Information */}
            <Box
                sx={{
                    py: { xs: 3, md: 6 },
                    px: { xs: 2, md: 4 },
                    backgroundColor: surfaceColor,
                }}
            >
                <Box sx={{ maxWidth: { xs: '100%', md: '800px' }, mx: 'auto' }}>
                    <Grid container spacing={{ xs: 3, md: 4 }}>
                        {/* Social Links */}
                        {card?.social_profiles && card.social_profiles.length > 0 && (
                            <Grid item xs={12} md={6}>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontSize: { xs: '1.3rem', md: '2rem' },
                                        fontWeight: 700,
                                        mb: { xs: 2, md: 3 },
                                        color: primaryColor,
                                        textAlign: { xs: 'center', md: 'left' },
                                    }}
                                >
                                    Follow Me
                                </Typography>
                                <Box sx={{ 
                                    display: 'flex', 
                                    gap: { xs: 1.5, md: 2 }, 
                                    flexWrap: 'wrap',
                                    justifyContent: { xs: 'center', md: 'flex-start' },
                                }}>
                                    {card.social_profiles.map((social, index) => (
                                        <Link
                                            key={index}
                                            href={social.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={onAnalyticsClick}
                                            sx={{
                                                width: { xs: 45, md: 50 },
                                                height: { xs: 45, md: 50 },
                                                borderRadius: { xs: '8px', md: '10px' },
                                                backgroundColor: cardColor,
                                                border: `1px solid ${borderColor}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    borderColor: primaryColor,
                                                    backgroundColor: `${primaryColor}20`,
                                                    transform: 'translateY(-3px)',
                                                }
                                            }}
                                        >
                                            {getSocialIcon(social.platform)}
                                        </Link>
                                    ))}
                                </Box>
                            </Grid>
                        )}

                        {/* Company Info & QR Code */}
                        <Grid item xs={12} md={card?.social_profiles?.length > 0 ? 6 : 12}>
                            <Box sx={{ textAlign: 'center', mt: { xs: 2, md: 0 } }}>
                                {/* Company Logo - Only show here if not in cover */}
                                {card?.company_logo_url && !card?.cover_photo_url && (
                                    <Box
                                        sx={{
                                            width: { xs: 60, md: 80 },
                                            height: { xs: 60, md: 80 },
                                            borderRadius: { xs: '8px', md: '12px' },
                                            overflow: 'hidden',
                                            mx: 'auto',
                                            mb: 2,
                                            backgroundColor: cardColor,
                                            border: `2px solid ${borderColor}`,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                backgroundImage: `url(${getImageUrl(card.company_logo_url)})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                            }}
                                        />
                                    </Box>
                                )}
                                
                                {card?.company_name && (
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            color: textPrimary,
                                            fontWeight: 600,
                                            mb: 2,
                                            fontSize: { xs: '1.2rem', md: '1.5rem' },
                                        }}
                                    >
                                        {card.company_name}
                                    </Typography>
                                )}

                                {card?.address && (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        mb: 3,
                                        flexWrap: 'wrap',
                                        gap: 1,
                                    }}>
                                        <LocationOn sx={{ color: primaryColor, fontSize: { xs: 18, md: 20 } }} />
                                        <Typography sx={{ 
                                            color: textSecondary, 
                                            fontSize: { xs: '0.8rem', md: '0.9rem' },
                                            textAlign: 'center',
                                        }}>
                                            {card.address}
                                        </Typography>
                                    </Box>
                                )}

                                {/* QR Code */}
                                {qrCodeValue && (
                                    <Paper
                                        sx={{
                                            p: { xs: 1.5, md: 2 },
                                            backgroundColor: textPrimary,
                                            borderRadius: { xs: '8px', md: '12px' },
                                            display: 'inline-block',
                                            border: `2px solid ${primaryColor}`,
                                        }}
                                    >
                                        <Box
                                            dangerouslySetInnerHTML={{ __html: qrCodeValue }}
                                            sx={{
                                                '& svg': {
                                                    width: { xs: 100, md: 120 },
                                                    height: { xs: 100, md: 120 },
                                                    display: 'block'
                                                }
                                            }}
                                        />
                                    </Paper>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
};

export default MinimalistTheme; 